import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { PROGRAMMING_SCORING_TYPE_OPTIONS } from '@ergshoot/shared';
import { Search, X } from 'lucide-react';

export interface FilterConfig {
  showSearch?: boolean;
  showCategory?: boolean;
  showScoring?: boolean;
  showDateRange?: boolean;
  showDate?: boolean;
  showEquipment?: boolean;
  categoryOptions?: Array<{ value: string; label: string }>;
}

export interface FilterValues {
  search: string;
  category: string;
  scoring_type: string;
  date_from: string;
  date_to: string;
  equipment: string;
}

interface FilterBarProps {
  config: FilterConfig;
  values: FilterValues;
  onChange: (values: FilterValues) => void;
  resultCount?: number;
}

const defaultScoringOptions = [
  { value: '', label: 'All Scoring Types' },
  ...PROGRAMMING_SCORING_TYPE_OPTIONS,
];

export function FilterBar({ config, values, onChange, resultCount }: FilterBarProps) {
  const hasActiveFilters =
    values.search ||
    values.category ||
    values.scoring_type ||
    values.date_from ||
    values.date_to ||
    values.equipment;

  const clearFilters = () => {
    onChange({
      search: '',
      category: '',
      scoring_type: '',
      date_from: '',
      date_to: '',
      equipment: '',
    });
  };

  const update = (key: keyof FilterValues, value: string) => {
    onChange({ ...values, [key]: value });
  };

  return (
    <div className="space-y-3 mb-6 p-4 rounded-lg border border-border bg-card">
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        {config.showSearch !== false && (
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search workouts..."
              value={values.search}
              onChange={(e) => update('search', e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {/* Category */}
        {config.showCategory && config.categoryOptions && (
          <div className="min-w-[160px]">
            <Select
              options={[{ value: '', label: 'All Categories' }, ...config.categoryOptions]}
              value={values.category}
              onChange={(e) => update('category', e.target.value)}
            />
          </div>
        )}

        {/* Scoring Type */}
        {config.showScoring && (
          <div className="min-w-[160px]">
            <Select
              options={defaultScoringOptions}
              value={values.scoring_type}
              onChange={(e) => update('scoring_type', e.target.value)}
            />
          </div>
        )}
      </div>

      {/* Second row: date + equipment */}
      {(config.showDate || config.showDateRange || config.showEquipment) && (
        <div className="flex flex-wrap gap-3">
          {config.showDate && (
            <div className="min-w-[150px]">
              <Input
                type="date"
                value={values.date_from}
                onChange={(e) => {
                  onChange({ ...values, date_from: e.target.value, date_to: e.target.value });
                }}
              />
            </div>
          )}
          {config.showDateRange && !config.showDate && (
            <>
              <div className="min-w-[150px]">
                <Input
                  type="date"
                  placeholder="From"
                  value={values.date_from}
                  onChange={(e) => update('date_from', e.target.value)}
                />
              </div>
              <div className="min-w-[150px]">
                <Input
                  type="date"
                  placeholder="To"
                  value={values.date_to}
                  onChange={(e) => update('date_to', e.target.value)}
                />
              </div>
            </>
          )}
          {config.showEquipment && (
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Filter by equipment..."
                value={values.equipment}
                onChange={(e) => update('equipment', e.target.value)}
              />
            </div>
          )}
        </div>
      )}

      {/* Active filters indicator */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-3 w-3 mr-1" />
            Clear filters
          </Button>
          {resultCount !== undefined && <Badge variant="secondary">{resultCount} results</Badge>}
        </div>
      )}
    </div>
  );
}

export const emptyFilterValues: FilterValues = {
  search: '',
  category: '',
  scoring_type: '',
  date_from: '',
  date_to: '',
  equipment: '',
};
